import { minBy } from "lodash";
import { geom2 } from "./geom2";
import { vec2 } from "./vec2";
export class seg2 {
    /**
     * Determines if two line segments are colinear.
     * @param s1 - The first line segment.
     * @param s2 - The second line segment.
     * @param precision - The precision used for the comparison. Defaults to 9.
     * @returns True if the line segments are colinear, false otherwise.
     */
    static colinear(s1, s2, precision = 9) {
        const epsilon = 10 ** -precision;
        return (geom2.triangleArea(s1[0], s1[1], s2[0]) < epsilon &&
            geom2.triangleArea(s1[0], s1[1], s2[1]) < epsilon &&
            geom2.triangleArea(s2[0], s2[1], s1[0]) < epsilon);
    }
    /**
     * Checks if two line segments overlap.
     * @param s1 - The first line segment.
     * @param s2 - The second line segment.
     * @param precision - The precision used for colinearity check. Default is 9.
     * @returns `true` if the line segments overlap, `false` otherwise.
     */
    static overlap(s1, s2, precision = 9) {
        if (!seg2.colinear(s1, s2, precision))
            return false;
        return (Math.max(s1[0].x, s1[1].x) >= Math.min(s2[0].x, s2[1].x) &&
            Math.max(s2[0].x, s2[1].x) >= Math.min(s1[0].x, s1[1].x) &&
            Math.max(s1[0].y, s1[1].y) >= Math.min(s2[0].y, s2[1].y) &&
            Math.max(s2[0].y, s2[1].y) >= Math.min(s1[0].y, s1[1].y));
    }
    /**
     * Calculates the slope of a line segment.
     *
     * @param s - The line segment.
     * @returns The slope of the line segment.
     */
    static slope(s) {
        const v = vec2.sub(s[1], s[0]);
        return v.y / v.x;
    }
    /**
     * Calculates the intersection point of two line segments.
     *
     * Return intersection point of infinite lines defined by the given segments.
     * To also ensure the intersection point is within the segments, use seg2.intersection()
     * instead.
     *
     * @param s1 The first line segment.
     * @param s2 The second line segment.
     * @returns The intersection point as an object with `x` and `y` coordinates, or `undefined` if there is no intersection.
     */
    static lineIntersection(s1, s2) {
        const slope1 = seg2.slope(s1);
        const intercept1 = s1[0].y - slope1 * s1[0].x;
        const slope2 = seg2.slope(s2);
        const intercept2 = s2[0].y - slope2 * s2[0].x;
        if (slope1 === slope2 || !seg2.len(s1) || !seg2.len(s2)) {
            // Parallel lines or one or both of the segments is zero-length; no single point of
            // intersection. If the caller wants to detect overlapping/colinear lines, the caller
            // must do that separately.
            return undefined;
        }
        let x = (intercept1 - intercept2) / (slope2 - slope1);
        let [slope, intercept] = [slope1, intercept1];
        if (Math.abs(slope1) === Infinity) {
            [x, slope, intercept] = [s1[0].x, slope2, intercept2];
        }
        else if (Math.abs(slope2) === Infinity) {
            [x, slope, intercept] = [s2[0].x, slope1, intercept1];
        }
        return { x, y: slope * x + intercept };
    }
    /**
     * Calculates the intersection point between two line segments.
     *
     * @param s1 - The first line segment.
     * @param s2 - The second line segment.
     * @returns The intersection point if it exists, otherwise undefined.
     */
    static intersection(s1, s2) {
        if (seg2.colinear(s1, s2) && seg2.overlap(s1, s2)) {
            // If the segments are colinear and overlap, return where the first segment first
            // hits the second. Order is important -- we assume segments are ordered in a
            // direction that matters to the caller (eg for a placeWire() call that cares about
            // direction of a path)
            if (isBetween(s1[0].x, [s2[0].x, s2[1].x])) {
                return s1[0];
            }
            return minBy(s2, (p) => vec2.distance(p, s1[0]));
        }
        const intersection = seg2.lineIntersection(s1, s2);
        if (!intersection)
            return undefined;
        if (isBetween(intersection.x, [s1[0].x, s1[1].x]) &&
            isBetween(intersection.x, [s2[0].x, s2[1].x]) &&
            isBetween(intersection.y, [s1[0].y, s1[1].y]) &&
            isBetween(intersection.y, [s2[0].y, s2[1].y])) {
            return intersection;
        }
        return undefined;
    }
    /**
     * Calculates the length of a line segment.
     *
     * @param segment - The line segment to calculate the length of.
     * @returns The length of the line segment.
     */
    static len(segment) {
        return vec2.distance(segment[1], segment[0]);
    }
    /**
     * Calculates the distance between a point and a line segment.
     *
     * @param point - The point.
     * @param segment - The line segment.
     * @returns The distance between the point and the line segment.
     */
    static distanceToPoint(point, segment) {
        const [p1, p2] = segment;
        // Line equation coefficients: Ax + By + C = 0
        const A = p1.y - p2.y;
        const B = p2.x - p1.x;
        const C = p1.x * p2.y - p2.x * p1.y;
        // Compute perpendicular distance
        return Math.abs(A * point.x + B * point.y + C) / Math.sqrt(A * A + B * B);
    }
    /**
     * Calculates whether a point is on a line segment.
     *
     * @param point - The point.
     * @param segment - The line segment.
     * @param tolerance - The tolerance for the distance check. Defaults to 10 ** -9.
     * @returns `true` if the point is on the line segment, `false` otherwise.
     */
    static pointOnSegment(point, segment, tolerance = 10 ** -9) {
        return (isBetween(point.x, [segment[0].x, segment[1].x]) &&
            isBetween(point.y, [segment[0].y, segment[1].y]) &&
            seg2.distanceToPoint(point, segment) < tolerance);
    }
}
function isBetween(n, range) {
    const floatingPointTolerance = 10 ** -9;
    let [start, end] = range;
    // Ensure range is ordered
    if (start > end) {
        [start, end] = [end, start];
    }
    start -= floatingPointTolerance;
    end += floatingPointTolerance;
    // >= <= is important for exact endpoint matching.
    return n >= start && n <= end;
}
//# sourceMappingURL=seg2.js.map